/**
 * What the AI features cost (Phase 10): the meter, the budget and the
 * dashboard's shapes, shared so the page reads the same numbers the server
 * refuses by.
 *
 * Every call to a paid service is written down as it happens — Deepgram's
 * seconds of audio, Claude's tokens — with what it is estimated to have
 * cost at list price. A room's calls count against the room's budget for
 * the calendar month (UTC); a video's calls count against its owner's. A
 * server-wide cap sits above both, for development. The check is here and
 * pure: spent so far against the cap, nothing else.
 */

export const AI_FEATURES = [
  'captions',
  'transcript_fixes',
  'notes',
  'assistant',
  'action_items',
  'board_proposals',
  'video_transcript',
  'video_insights',
  'video_editing',
] as const
export type AiFeature = (typeof AI_FEATURES)[number]

export const AI_FEATURE_LABELS: Record<AiFeature, string> = {
  captions: 'Note taker (listening)',
  transcript_fixes: 'Note taker (fixing words)',
  notes: 'Meeting notes',
  assistant: 'Assistant',
  action_items: 'Live action items',
  board_proposals: 'Suggested ticket updates',
  video_transcript: 'Video transcripts',
  video_insights: 'Video summaries',
  video_editing: 'AI video editing',
}

/** The features a room pays for, and the ones a video's owner pays for. */
export const ROOM_AI_FEATURES: readonly AiFeature[] = [
  'captions',
  'transcript_fixes',
  'notes',
  'assistant',
  'action_items',
  'board_proposals',
]
export const VIDEO_AI_FEATURES: readonly AiFeature[] = [
  'video_transcript',
  'video_insights',
  'video_editing',
]

/** Deepgram, Anthropic, or the editor's AI provider as an admin chose it (AI_PROVIDERS). */
export type AiProvider = 'deepgram' | 'anthropic' | (string & {})

/** The first instant of the calendar month `now` falls in, UTC. */
export function monthStartUtc(now: number): number {
  const d = new Date(now)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)
}

/** `2026-09`, for the month `now` falls in. */
export function monthKey(now: number): string {
  const d = new Date(now)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/** `September 2026`. */
export function monthName(now: number): string {
  return new Date(now).toLocaleString('en', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

export interface BudgetInput {
  /** This month's spend by whoever pays: the room, or the video's owner. */
  spentUsd: number
  /** Their cap for the month. 0 allows nothing. */
  capUsd: number
  /** This month's spend across the whole server. */
  globalSpentUsd: number
  /** The server's cap; 0 = none. */
  globalCapUsd: number
  /** Who pays, for the message. */
  payer: 'room' | 'user'
  now: number
}

export type BudgetVerdict =
  | { allowed: true; remainingUsd: number; fraction: number }
  | {
      allowed: false
      code: 'budget_reached' | 'server_budget_reached'
      reason: string
      remainingUsd: 0
      fraction: number
    }

export const usd = (x: number): string =>
  x > 0 && x < 0.01 ? `$${x.toFixed(4)}` : `$${x.toFixed(2)}`

/**
 * May one more AI request start? Only while what has been spent is under
 * the cap: a request already running finishes, so a month can end a call
 * over, never more.
 */
export function budgetVerdict(b: BudgetInput): BudgetVerdict {
  const fraction = b.capUsd > 0 ? Math.min(1, b.spentUsd / b.capUsd) : 1
  if (b.globalCapUsd > 0 && b.globalSpentUsd >= b.globalCapUsd) {
    return {
      allowed: false,
      code: 'server_budget_reached',
      reason: `This server has used its AI budget for ${monthName(b.now)} (${usd(b.globalCapUsd)}).`,
      remainingUsd: 0,
      fraction,
    }
  }
  if (b.spentUsd >= b.capUsd) {
    const who =
      b.payer === 'room'
        ? `This room has used its AI budget for ${monthName(b.now)}`
        : `You have used your AI budget for ${monthName(b.now)}`
    const fix =
      b.payer === 'room'
        ? ' A host can raise it in the room’s AI settings.'
        : ' It resets on the first of the month.'
    return {
      allowed: false,
      code: 'budget_reached',
      reason: `${who} (${usd(b.spentUsd)} of ${usd(b.capUsd)}).${fix}`,
      remainingUsd: 0,
      fraction,
    }
  }
  return { allowed: true, remainingUsd: b.capUsd - b.spentUsd, fraction }
}

/** Why an AI feature will not run in a room that has them switched off. */
export const AI_OFF_REASON = 'AI features are turned off for this room.'

/** One feature's month: calls, units, cost. */
export interface AiFeatureUsage {
  feature: AiFeature
  calls: number
  /** Audio sent to be transcribed, ms. */
  audioMs: number
  inputTokens: number
  outputTokens: number
  costUsd: number
}

/** One day of the month (UTC), each feature's cost. */
export interface AiUsageDay {
  /** `YYYY-MM-DD` */
  day: string
  costs: Partial<Record<AiFeature, number>>
  totalUsd: number
}

/** `GET /api/ai/rooms/:slug/usage` and `GET /api/ai/me/usage`. */
export interface AiUsageReport {
  /** `YYYY-MM` */
  month: string
  monthName: string
  payer: 'room' | 'user'
  /** The features this payer can use, in their fixed order: the chart's series. */
  features: AiFeature[]
  byFeature: AiFeatureUsage[]
  /** Every day of the month so far, empty ones included. */
  daily: AiUsageDay[]
  budget: {
    capUsd: number
    /** Whether the cap is the room's own or the server's default. */
    custom: boolean
    defaultCapUsd: number
    spentUsd: number
    remainingUsd: number
    /** 0–1 */
    fraction: number
    reached: boolean
    /** Set when the reason AI will not run is the server's cap, not this one. */
    serverReached: boolean
  }
  /** Rooms only: the room's switch. */
  aiEnabled?: boolean
  /** Where the prices come from, for the footnote. */
  pricesAsOf: string
}

/** A room's AI settings as its hosts see them: `GET /api/ai/rooms/:slug`. */
export interface RoomAiSettings {
  aiEnabled: boolean
  /** The room's own cap, or null for the server's default. */
  monthlyBudgetUsd: number | null
  defaultBudgetUsd: number
  spentUsd: number
  capUsd: number
  captionsAvailable: boolean
  notesAvailable: boolean
  /** Days a meeting's recordings, transcript and notes are kept; 0 = for ever. */
  retentionDays: number
  /** The room's hosts change these; its cohosts see them. */
  canEdit: boolean
}
